import { useMemo } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { GOLD, NAVY } from "../styles/palette";
import { getPrice, getDeoMonthly } from "../lib/pricing";
import { ACTIVE_THEME } from "../themes";
import { DashboardApple } from "./DashboardApple";

// Recurring monthly revenue for one client incl. the active deodorizer sub (I2).
const clientMonthly = (c, pricing) =>
  getPrice(c.frequency, c.dogs, pricing) +
  (c.deo_enabled && c.deo_status === "active" ? getDeoMonthly(c.deo_frequency, pricing) : 0);

export function Dashboard({ clients, invoices, todayJobs, jobsDone, toggleJob, setPage, mrr, pricing, org, serviceArea, onSetup }) {
  const isMobile      = useIsMobile();
  const active        = clients.filter(c=>c.active).length;
  const paid          = invoices.filter(i=>i.status==="paid").length;
  const pendingAmt    = invoices.filter(i=>i.status!=="paid"&&i.status!=="draft").reduce((s,i)=>s+(Number(i.amount)||0),0);
  const needsApproval = invoices.filter(i=>i.status==="draft").length;
  const doneCount     = Object.values(jobsDone).filter(Boolean).length;
  const JobRow        = isMobile ? "label" : "div"; // phone: whole row toggles the job

  // Revenue metrics
  const avgRevenuePerClient = active > 0 ? mrr / active : 0;
  const nextMonthForecast   = clients.filter(c=>c.active&&c.frequency&&c.dogs).reduce((s,c)=>s+clientMonthly(c,pricing),0);

  // Churn tracking
  const now = new Date();
  const thisMonthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth()-1, 1).toISOString().split("T")[0]; // eslint-disable-line no-unused-vars
  const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split("T")[0]; // eslint-disable-line no-unused-vars
  // I3: count churn by the deactivation date (deactivated_at), NOT start_date.
  // start_date is when service began, so the old check counted clients who
  // SIGNED UP this month, the opposite of churn.
  const deactivatedThisMonth = clients.filter(c=>c.status==="deactivated"&&(c.deactivated_at||"")>=thisMonthStart).length;

  // YoY data — build monthly paid invoice totals by month
  const yoyData = useMemo(()=>{
    const thisYear = now.getFullYear();
    const lastYear = thisYear - 1;
    const months   = Array.from({length:12},(_,i)=>i);
    return months.map(m=>{
      const mo   = String(m+1).padStart(2,"0");
      // I4: gross revenue counts positive paid invoices only (match ReportsPage),
      // so a negative-amount credit invoice doesn't silently shrink the bar.
      const thisY = invoices.filter(i=>i.status==="paid"&&Number(i.amount)>0&&i.date?.startsWith(`${thisYear}-${mo}`)).reduce((s,i)=>s+Number(i.amount),0);
      const lastY = invoices.filter(i=>i.status==="paid"&&Number(i.amount)>0&&i.date?.startsWith(`${lastYear}-${mo}`)).reduce((s,i)=>s+Number(i.amount),0);
      return { month:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m], thisY, lastY };
    });
  }, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revenue trend — last 6 months actual paid
  const revData = useMemo(()=>{
    return Array.from({length:6},(_,i)=>{
      const d  = new Date(now.getFullYear(), now.getMonth()-5+i, 1);
      const mo = String(d.getMonth()+1).padStart(2,"0");
      const yr = d.getFullYear();
      return invoices.filter(inv=>inv.status==="paid"&&Number(inv.amount)>0&&inv.date?.startsWith(`${yr}-${mo}`)).reduce((s,i)=>s+Number(i.amount),0);
    });
  }, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps
  const maxRev = Math.max(...revData, mrr, 1);

  const ACTIVITY = [
    { color:"#36b37e", text:`${clients.filter(c=>c.active).length} clients active this month`,      time:"Today"     },
    { color:GOLD,      text:`${needsApproval} invoice${needsApproval!==1?"s":""} awaiting approval`, time:"Today"     },
    { color:"#3B82F6", text:`${todayJobs.length} jobs scheduled for today`,                          time:"Today"     },
    { color:NAVY,      text:`$${mrr.toFixed(2)} projected monthly revenue`,                         time:"This month" },
    { color:"#8B5CF6", text:`${invoices.filter(i=>i.status==="paid").length} invoices paid`,        time:"This month" },
  ];

  // The apple-native TEMPLATE owns its own dashboard layout (a real information
  // redesign, not a reskin). Same computed data + handlers, zero functional change.
  // "default" falls through to the original markup below — byte-identical rollback.
  if (ACTIVE_THEME === "apple-native") {
    return <DashboardApple
      clients={clients} invoices={invoices} todayJobs={todayJobs} jobsDone={jobsDone}
      toggleJob={toggleJob} setPage={setPage} mrr={mrr} pricing={pricing}
      org={org} serviceArea={serviceArea} onSetup={onSetup}
      active={active} paid={paid} pendingAmt={pendingAmt} needsApproval={needsApproval}
      doneCount={doneCount} avgRevenuePerClient={avgRevenuePerClient}
      nextMonthForecast={nextMonthForecast} deactivatedThisMonth={deactivatedThisMonth}
      revData={revData}
    />;
  }

  return (
    <div>
      {/* Stats */}
      <div className="stats-row">
        {[
          {label:"Active Clients",      value:active,                              sub:`${clients.length} total`,              trend:`${clients.filter(c=>!c.active).length} inactive`, up:true},
          {label:"Current MRR",         value:`$${mrr.toFixed(2)}`,               sub:"active subscriptions",                 trend:`Next month: $${nextMonthForecast.toFixed(2)}`, up:true},
          {label:"Avg Revenue/Client",  value:`$${avgRevenuePerClient.toFixed(2)}`,sub:"per active client/month",              trend:"Based on current plans", up:true},
          {label:"Jobs Today",          value:todayJobs.length,                    sub:`${doneCount} completed`,                trend:doneCount===todayJobs.length&&todayJobs.length>0?"All done! 🎉":"In progress", up:true},
          {label:"Pending Payments",    value:`$${pendingAmt.toFixed(2)}`,         sub:`${invoices.length-paid} invoices`,      trend:needsApproval>0?`${needsApproval} need approval`:"All clear", up:needsApproval===0},
          {label:"Deactivated (Month)", value:deactivatedThisMonth,               sub:"churn this month",                     trend:deactivatedThisMonth===0?"No churn 🎉":"Review clients", up:deactivatedThisMonth===0},
        ].map(s=>(
          <div key={s.label} className="stat-card" style={{cursor:"default"}}>
            <div className="stat-accent"/>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
            <div className={`stat-trend ${s.up?"trend-up":"trend-down"}`}>{s.trend}</div>
          </div>
        ))}
      </div>

      {/* Today banner */}
      <div className="today-banner">
        <div>
          <h3>{todayJobs.length>0?`You have ${todayJobs.length} jobs today`:"No jobs scheduled today"}</h3>
          <p>{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} {doneCount>0&&`· ${doneCount} of ${todayJobs.length} done`}</p>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:12}}>
            {todayJobs.length>0&&<button className="btn btn-gold btn-sm" onClick={()=>setPage("route")}>🗺️ View Route</button>}
            {needsApproval>0&&<button className="btn btn-sm" style={{background:"rgba(244,193,32,.2)",color:GOLD,border:"none"}} onClick={()=>setPage("invoices")}>⚠️ {needsApproval} Approval{needsApproval>1?"s":""} Pending</button>}
          </div>
        </div>
        <div className="today-num">{todayJobs.length}</div>
      </div>

      {/* Charts + Activity */}
      <div className="grid-2" style={{marginBottom:16}}>
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div className="card-title" style={{marginBottom:0}}>Revenue — Last 6 Months</div>
            <span className="badge badge-green">Actual paid invoices</span>
          </div>
          <div className="chart-bar-wrap">
            {revData.map((val,i)=>{
              const d = new Date(now.getFullYear(), now.getMonth()-5+i, 1);
              const lbl = d.toLocaleDateString("en-US",{month:"short"});
              return (
                <div key={i} className="chart-bar-col">
                  <div className="chart-bar">
                    <div className="chart-bar-fill" style={{height:`${(val/maxRev)*100}%`,background:i===5?GOLD:NAVY}}/>
                  </div>
                  <div className="chart-label">{lbl}</div>
                </div>
              );
            })}
          </div>
          <div style={{marginTop:10,display:"flex",justifyContent:"space-between",fontSize:11,color:"#8993a4"}}>
            <span>6-mo total: <strong style={{color:"#1a1f36"}}>${revData.reduce((a,b)=>a+b,0).toLocaleString()}</strong></span>
            <span>This month: <strong style={{color:GOLD}}>${revData[5].toLocaleString()}</strong></span>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Activity</div>
          {ACTIVITY.map((a,i)=>(
            <div key={i} className="activity-item">
              <div className="activity-dot" style={{background:a.color}}/>
              <div>
                <div className="activity-text">{a.text}</div>
                <div className="activity-time">{a.time}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div className="card" style={{marginBottom:16}}>
        <div className="card-title">Quick Actions</div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(auto-fit,minmax(100px,1fr))":"repeat(6,1fr)",gap:10}}>
          {[
            {icon:"➕", label:"Add Client",     action:()=>setPage("clients")},
            {icon:"📋", label:"View Schedule",  action:()=>setPage("schedule")},
            {icon:"🗺️", label:"Today's Route",  action:()=>setPage("route")},
            {icon:"📍", label:"Client Map",     action:()=>setPage("clientmap")},
            {icon:"💰", label:"Invoices",       action:()=>setPage("invoices")},
          ].map(q=>(
            <div key={q.label} className="quick-action" onClick={q.action}>
              <div className="qa-icon">{q.icon}</div>
              <span>{q.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Today's jobs + Top clients */}
      <div className="grid-2">
        <div className="card">
          <div className="card-title">Today's Jobs ({todayJobs.length})</div>
          {todayJobs.length===0
            ? <div className="empty"><p>No jobs today 🎉</p></div>
            : todayJobs.map((j,i)=>(
              <JobRow key={j.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",minHeight:isMobile?48:undefined,borderBottom:i<todayJobs.length-1?"1px solid #f4f5f7":"none"}}>
                <input type="checkbox" checked={!!jobsDone[j.id]} onChange={()=>toggleJob(j.id)} style={{accentColor:NAVY,width:isMobile?22:undefined,height:isMobile?22:undefined,flexShrink:0,margin:isMobile?0:undefined}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:13,textDecoration:jobsDone[j.id]?"line-through":"none",color:jobsDone[j.id]?"#b3bac5":NAVY}}>{j.client_name||j.clientName}</div>
                  <div style={{fontSize:11,color:"#8993a4"}}>{j.client_address||j.address} · {j.dogs} dog{j.dogs>1?"s":""}</div>
                </div>
                {j.notes&&<span style={{fontSize:10,background:"#fffae6",color:"#974f0c",padding:"2px 6px",borderRadius:4}}>📝</span>}
              </JobRow>
            ))
          }
        </div>

        <div className="card">
          <div className="card-title">Top Clients by Revenue</div>
          {clients.filter(c=>c.active).sort((a,b)=>clientMonthly(b,pricing)-clientMonthly(a,pricing)).slice(0,6).map((c,i)=>(
            <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:i<5?"1px solid #f4f5f7":"none"}}>
              <div style={{width:22,height:22,borderRadius:"50%",background:NAVY,color:GOLD,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{i+1}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600,fontSize:12}}>{c.name}</div>
                <div style={{fontSize:11,color:"#8993a4",textTransform:"capitalize"}}>{c.frequency} · {c.dogs} dog{c.dogs>1?"s":""}</div>
              </div>
              <div style={{fontWeight:700,fontSize:13,color:NAVY}}>${clientMonthly(c,pricing).toFixed(2)}</div>
            </div>
          ))}
          {clients.filter(c=>c.active).length===0&&<div className="empty"><p>No active clients yet</p></div>}
        </div>
      </div>

      {/* Year over Year Comparison */}
      <div className="card" style={{marginTop:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div className="card-title" style={{marginBottom:0}}>📊 Year over Year — Monthly Revenue</div>
          <div style={{display:"flex",gap:12,fontSize:11}}>
            <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:2,background:NAVY,display:"inline-block"}}/>{new Date().getFullYear()}</span>
            <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:10,height:10,borderRadius:2,background:"#93c5fd",display:"inline-block"}}/>{new Date().getFullYear()-1}</span>
          </div>
        </div>
        {/* Phone: 12 paired columns can't read at ~25px each, so the chart becomes a
            horizontally-scrollable strip (min 480px) and prints this-year values under
            the months — title= tooltips are unreachable on touch. */}
        <div style={{overflowX:isMobile?"auto":"visible",WebkitOverflowScrolling:"touch",margin:isMobile?"0 -4px":0,padding:isMobile?"0 4px":0}}>
        <div style={{display:"flex",gap:6,alignItems:"flex-end",height:isMobile?132:120,minWidth:isMobile?480:0}}>
          {yoyData.map(({month,thisY,lastY},i)=>{
            const maxY = Math.max(...yoyData.map(d=>Math.max(d.thisY,d.lastY)),1);
            return (
              <div key={month} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                <div style={{width:"100%",display:"flex",gap:1,alignItems:"flex-end",height:90}}>
                  <div style={{flex:1,background:"#93c5fd",borderRadius:"2px 2px 0 0",height:`${(lastY/maxY)*90}px`,minHeight:lastY>0?2:0,transition:"height .3s"}} title={`${new Date().getFullYear()-1}: $${lastY.toFixed(0)}`}/>
                  <div style={{flex:1,background:NAVY,borderRadius:"2px 2px 0 0",height:`${(thisY/maxY)*90}px`,minHeight:thisY>0?2:0,transition:"height .3s"}} title={`${new Date().getFullYear()}: $${thisY.toFixed(0)}`}/>
                </div>
                <div style={{fontSize:9,color:"#8993a4",textAlign:"center"}}>{month}</div>
                {isMobile&&<div style={{fontSize:9,fontWeight:600,color:NAVY,textAlign:"center",whiteSpace:"nowrap"}}>{thisY>=1000?`$${(thisY/1000).toFixed(1)}k`:`$${thisY.toFixed(0)}`}</div>}
              </div>
            );
          })}
        </div>
        </div>
        <div style={{marginTop:8,display:"flex",flexDirection:isMobile?"column":"row",flexWrap:"wrap",gap:isMobile?4:8,justifyContent:"space-between",fontSize:11,color:"#8993a4"}}>
          <span>This year total: <strong style={{color:NAVY}}>${yoyData.reduce((s,d)=>s+d.thisY,0).toLocaleString()}</strong></span>
          <span>Last year total: <strong style={{color:"#42526e"}}>${yoyData.reduce((s,d)=>s+d.lastY,0).toLocaleString()}</strong></span>
          <span>Growth: <strong style={{color:yoyData.reduce((s,d)=>s+d.thisY,0)>=yoyData.reduce((s,d)=>s+d.lastY,0)?"#006644":"#bf2600"}}>
            {yoyData.reduce((s,d)=>s+d.lastY,0)>0?((yoyData.reduce((s,d)=>s+d.thisY,0)/yoyData.reduce((s,d)=>s+d.lastY,0)-1)*100).toFixed(1)+"%":"N/A"}
          </strong></span>
        </div>
      </div>
    </div>
  );
}
